import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api';

import { SearchOptions } from '../model/searchOptions';
import { Rodeo } from '../model/rodeo';
import { PayoutConfig } from 'model/payoutConfig';
import { PayoutSource } from 'model/payout-source';

@Injectable()
export class RodeoService {
    public selectedRodeo = new BehaviorSubject<Rodeo | null>(null);

    constructor(private api: ApiService) { }

    getRodeoSettingOptions() {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.rodeos + '/getSettingOptions')
                .then(data => resolve(data), error => reject(error));
        });
    }
    
    getSecretaries() {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.search + '/secretaries')
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRodeos() {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.rodeos + '/getAll')
                .then(data => resolve(data), error => reject(error));
        });
    }

    sendRunNotifications(rodeoId, rodeoEventId, scheduleId, stockPublish) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + `/sendRunNotifications?rodeoId=${rodeoId}&rodeoEventId=${rodeoEventId}&scheduleId=${scheduleId}&stockPublish=${stockPublish}`)
                .then(data => resolve(data), error => reject(error));
        });
    }

    unpublish(rodeoId, rodeoEventId, scheduleId, stockPublish) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + `/unpublish?rodeoId=${rodeoId}&rodeoEventId=${rodeoEventId}&scheduleId=${scheduleId}&stockPublish=${stockPublish}`)
                .then(data => resolve(data), error => reject(error));
        });
    }

    uploadMembers(file, orgId) {
        let formData = new FormData();
        formData.append('fileUpload', file);

        return new Promise((resolve, reject) => {
            this.api
                .postWithAuthAndContentType(this.api.endpoints.rodeos + '/uploadMembers?orgId=' + orgId, formData)
                .then(data => resolve(data), error => reject(error));
        });
    }

    importDTAFile(file, rodeoId) {
        let formData = new FormData();
        formData.append('fileUpload', file);

        return new Promise((resolve, reject) => {
            this.api
                .postWithAuthAndContentType(this.api.endpoints.rodeos + '/importDTAFile?rodeoId=' + rodeoId, formData)
                .then(data => resolve(data), error => reject(error));
        });
    }

    updateRodeoPhotoPrice(rodeoId, photoPrice) {
        console.log("photoPRice: ", photoPrice);
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/updatePhotoPrice?id=' + rodeoId + "&photoPrice=" + photoPrice)
                .then(data => resolve(data), error => reject(error));
        });
    }

    searchForArena(arenaName) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/searchForArena?searchValue=' + arenaName)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getCurrentRodeos(searchOptions) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getCurrent', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    searchRodeos(searchOptions) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/searchRodeos', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getUpcomingRodeos(searchOptions) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getUpcoming', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRodeosOpenForRegistrations(searchOptions) {
        if (searchOptions == null)
            searchOptions = new SearchOptions();

        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getAllOpenForRegistration', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getCompletedRodeos(searchOptions) {
        if (searchOptions == null)
            searchOptions = new SearchOptions();

        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getCompletedRodeos', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRodeosInProgress(searchOptions) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getInProgressRodeos', searchOptions)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRodeo(rodeoId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.rodeos + '/get?id=' + rodeoId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    createRodeo(rodeo: Rodeo) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/create', rodeo)
                .then((createdRodeo: Rodeo) => {
                    this.selectedRodeo.next(createdRodeo);
                    resolve(createdRodeo);
                })
                .catch(error => reject(error));
        });
    }

    updateRodeo(data: any) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/update?id=' + data.id, data)
                .then((updatedRodeo: Rodeo) => {
                    this.selectedRodeo.next(updatedRodeo); 
                    resolve(updatedRodeo);
                })
                .catch(error => reject(error));
        });
    }

    deleteRodeo(rodeoId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .deleteWithAuth(this.api.endpoints.rodeos + '/delete?id=' + rodeoId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getOpenForTrade(accountId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/getOpenForTrade?accountId=' + accountId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    drawOrgRodeos(orgId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/drawOrgRodeos?orgId=' + orgId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    drawRodeos(rodeoId: any, rodeoEventId: any = null, wipeExisting: boolean = false) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/drawRodeos?rodeoId=' + rodeoId + '&rodeoEventId=' + rodeoEventId + '&wipeExisting=' + wipeExisting)
                .then(data => resolve(data), error => reject(error));
        });
    }

    draw(drawConfig: any) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/draw', drawConfig)
                .then(data => resolve(data), error => reject(error));
        });
    }

    undoOrgDraw(orgId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/undoOrgDraw?orgId=' + orgId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    undoDraw() {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/undoDraw')
                .then(data => resolve(data), error => reject(error));
        });
    }

    drawBackNumbers(rodeoId: any, discipline: any = null) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/drawBackNumbers?rodeoId=' + rodeoId + '&discipline=' + discipline)
                .then(data => resolve(data), error => reject(error));
        });
    }

    simulateRodeos(maxTests, avgEntries, percentMultiRodeo, buddyChance, rodeoCount, turnoutChance, anyPrefChance) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.rodeos + '/SimulateDraw?maxtests=' + maxTests + '&avgEntries=' + avgEntries + '&percentMultiRodeo=' + percentMultiRodeo + '&buddyChance=' + buddyChance + '&rodeoCount=' + rodeoCount + '&turnoutChance=' + turnoutChance + '&anyPrefChance=' + anyPrefChance)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getStandings() {
        return new Promise((resolve, reject) => {
            this.api
                .getWithAuth(this.api.endpoints.rodeos + '/getStandings')
                .then(data => resolve(data), error => reject(error));
        });
    }

    async addPayoutConfig(payoutConfig: PayoutConfig) : Promise<PayoutConfig> {
        return await this.api.postWithAuth<PayoutConfig>(`${this.api.endpoints.rodeos}/addPayoutConfig`, payoutConfig);
    }

    async deletePayoutConfig(payoutConfig: PayoutConfig) : Promise<PayoutConfig> {
        return await this.api.postWithAuth<PayoutConfig>(`${this.api.endpoints.rodeos}/deletePayoutConfig?payoutConfigId=${payoutConfig.id}`);
    }

    async updatePayoutConfig(payoutConfig: PayoutConfig) : Promise<PayoutConfig> {
        return await this.api.postWithAuth<PayoutConfig>(`${this.api.endpoints.rodeos}/updatePayoutConfig`, payoutConfig);
    }

    async updatePayoutSource(payoutSource: PayoutSource) : Promise<PayoutSource> {
        return await this.api.postWithAuth<PayoutSource>(`${this.api.endpoints.rodeos}/updatePayoutSource`, payoutSource);
    }
}



// WEBPACK FOOTER //
// ./src/services/rodeo.service.ts